import { useState } from 'react';
import { NavLink, Outlet, useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Activity, FileText, Brain, Settings, GraduationCap, Menu, X, Home, LogOut, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function PresenceLayout() {
  const { presenceId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const toggleLanguage = () => {
    const newLang = i18n.language === 'tr' ? 'en' : 'tr';
    i18n.changeLanguage(newLang);
  };

  const tabs = [
    { path: 'chat', label: t('presence.chat'), icon: MessageCircle },
    { path: 'activity', label: t('presence.activity'), icon: Activity },
    { path: 'memory', label: t('presence.memory'), icon: Brain },
    { path: 'consent', label: t('presence.consent'), icon: FileText },
    { path: 'settings', label: t('presence.settings'), icon: Settings },
    { path: 'training', label: t('presence.training'), icon: GraduationCap },
  ];

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="h-screen bg-slate-950 text-slate-50 flex overflow-hidden">
      {/* Mobile Menu Button - Only visible on mobile */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-900 text-cyan-400 hover:bg-slate-800 transition-colors"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={closeMobileMenu}
        />
      )}

      {/* Left Sidebar - Navigation */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-60 border-r border-slate-900 bg-[#06090f] flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo & Back - Top Section */}
        <div className="h-16 border-b border-slate-900 px-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_layout-kit-1/artifacts/fbm0hm7y_Represenx_logo_trans.png" 
              alt="RepresenX" 
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-cyan-400 transition-colors"
              title={i18n.language === 'tr' ? 'Switch to English' : 'Türkçeye Geç'}
            >
              <Globe size={18} />
            </button>
            <button
              onClick={() => navigate('/presences')}
              className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-cyan-400 transition-colors"
              title="Back to Presences"
            >
              <Home size={18} />
            </button>
          </div>
        </div>
        
        {/* Presence Identity */}
        <header className="h-14 border-b border-slate-900 px-5 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center text-sm font-semibold text-cyan-200">
            P
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/70 truncate">
              {t('presence.presencePrefix')}
            </div>
            <div className="text-sm font-semibold flex items-center gap-2 truncate">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              {presenceId.slice(0, 8)}
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <nav className="flex-1 pt-6 px-5 overflow-y-auto">
          <div className="space-y-1">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all',
                    isActive
                      ? 'bg-[#0b111a] text-cyan-50 border-l-2 border-cyan-400 shadow-sm'
                      : 'text-slate-400 hover:text-cyan-100 hover:bg-slate-900/50 hover:translate-x-1 border-l-2 border-transparent',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <tab.icon size={18} className={isActive ? 'text-cyan-400' : 'text-slate-500'} />
                    <span>{tab.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
        
        {/* Logout Button - Bottom */}
        <div className="p-5 border-t border-slate-900">
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/20"
          >
            <LogOut size={18} />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col lg:ml-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
